import { Search } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import './tool.css'
import './search-field.css'

/**
 * The search field — every table's first tool. A magnifier sits inside a
 * control-styled input, so search reads as part of the same set as the buttons
 * and the switch beside it.
 *
 * `collapsed` is for the phone's single row of chrome, where the row is a handful
 * of equal entry points: search becomes a button like its neighbors, and opening
 * it **expands the field over the row** rather than dropping a popover. It is the
 * one control here you type into, and typing wants the row's whole width — while a
 * popover would put a floating panel between you and the list you are filtering,
 * for a control that has nothing to choose from.
 *
 * It closes when you leave it empty-handed and stays open while it holds a query,
 * because a filter you cannot see is a list that looks broken.
 *
 * **`scope` puts what is being searched inside the field.** A list that
 * searches two things — the documents, or the words inside them — has to say
 * which, and a control at the other end of the toolbar cannot: the reader has
 * to notice it, connect it to a field several inches away, and remember the
 * connection. Inside the frame the relationship is structural. It is a slot
 * rather than a prop pair so the caller keeps its own vocabulary; what this
 * owns is the placement.
 */
export function SearchField({
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel = 'Search',
  collapsed = false,
  scope,
  id,
  'aria-invalid': invalid,
  'aria-describedby': describedBy,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  collapsed?: boolean
  /** What is being searched, shown inside the field's own frame. */
  scope?: ReactNode
  /** Set by a `Field`, so its label names the input. */
  id?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const expanded = !collapsed || open || value !== ''

  // Focus follows the expansion, so the tap that opens it is the only one needed.
  useEffect(() => {
    if (collapsed && open) inputRef.current?.focus()
  }, [collapsed, open])

  if (collapsed && !expanded) {
    return (
      <button
        type="button"
        className="searchfield__open"
        aria-label={ariaLabel}
        aria-expanded={false}
        onClick={() => setOpen(true)}
      >
        <Search size={18} aria-hidden />
        <span className="searchfield__word">Search</span>
      </button>
    )
  }

  return (
    <div
      className={`searchfield${collapsed ? ' searchfield--over' : ''}${
        scope ? ' searchfield--scoped' : ''
      }`}
      data-open={collapsed ? '' : undefined}
    >
      <span className="searchfield__icon">
        <Search size={16} aria-hidden />
      </span>
      <input
        ref={inputRef}
        id={id}
        type="search"
        className="control searchfield__input"
        aria-label={id ? undefined : ariaLabel}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return
          // Escape clears first, then closes — the same order as a browser's own
          // find bar, and it means one key both undoes and dismisses.
          if (value) onChange('')
          else setOpen(false)
        }}
      />
      {/* After the input, so the field still opens with a magnifier and the
          scope reads as a qualifier on what was typed rather than a prefix
          somebody has to get past to type at all. */}
      {scope ? <span className="searchfield__scope">{scope}</span> : null}
    </div>
  )
}
